import { FormEvent, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/lib/auth';
import * as authApi from '@/lib/auth-api';
import { AppShell } from '@/components/app/AppShell';
import { AvatarUploader } from '@/components/AvatarUploader';
import { MfaSettings } from '@/components/app/MfaSettings';
import { useMyEmployee, useSubmitChangeRequest } from '@/lib/employees';

export function ProfilePage() {
  const { t } = useTranslation();
  const { data: user, isLoading } = useCurrentUser();
  const qc = useQueryClient();

  const [displayName, setDisplayName] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) setDisplayName(user.displayName ?? '');
  }, [user]);

  const save = useMutation({
    mutationFn: () => authApi.updateProfile({
      displayName: displayName.trim() === '' ? undefined : displayName.trim(),
    }),
    onSuccess: (updated) => {
      qc.setQueryData(['currentUser'], updated);
      setSaved(true);
      setError(null);
      window.setTimeout(() => setSaved(false), 2400);
    },
    onError: (err) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to save.';
      setError(String(msg));
    },
  });

  if (isLoading) {
    return (
      <AppShell>
        <main className="mx-auto max-w-2xl px-6 py-10">
          <p className="text-sm text-muted-foreground">{t('common.loading', { defaultValue: 'Loading…' })}</p>
        </main>
      </AppShell>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <AppShell>
      <main className="mx-auto max-w-2xl px-6 py-10 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">
            {t('profile.title', { defaultValue: 'Profile' })}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('profile.subtitle', { defaultValue: 'Your account, sign-in info, and how teammates see you.' })}
          </p>
        </div>

        <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
          <AvatarUploader user={user} />
          <div className="border-t border-border pt-3">
            <div className="text-sm text-foreground truncate">{user.email}</div>
            <div className="mt-1.5 text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
              {t(`profile.role_${user.role}`, { defaultValue: user.role })}
            </div>
          </div>
        </section>

        <form onSubmit={onSubmit} className="rounded-2xl border border-border bg-card p-6 space-y-4">
          <h2 className="text-base font-semibold text-foreground">
            {t('profile.basics', { defaultValue: 'Basics' })}
          </h2>
          <div>
            <label htmlFor="display-name" className="block text-sm font-medium text-foreground mb-1">
              {t('profile.display_name', { defaultValue: 'Display name' })}
            </label>
            <input
              id="display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={120}
              className="w-full h-10 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          {error && (
            <div role="alert" className="rounded-lg border border-warn-200 bg-warn-50 text-warn-700 px-3 py-2 text-[13px]">{error}</div>
          )}
          {saved && (
            <div role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 px-3 py-2 text-[13px]">
              {t('profile.saved', { defaultValue: 'Saved.' })}
            </div>
          )}
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={save.isPending}
              className="rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {save.isPending ? t('common.saving', { defaultValue: 'Saving…' }) : t('common.save_changes', { defaultValue: 'Save changes' })}
            </button>
          </div>
        </form>

        <MyEmployeeSelfService />

        <MfaSettings />
      </main>
    </AppShell>
  );
}

/** Employee self-service (申請): view your linked HR record + request changes. */
function MyEmployeeSelfService() {
  const { t } = useTranslation();
  const me = useMyEmployee();
  const submit = useSubmitChangeRequest();
  const [preferredName, setPreferredName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [workLocation, setWorkLocation] = useState('');
  const [note, setNote] = useState('');
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (me.isLoading) return null;
  const emp = me.data;

  if (!emp) {
    return (
      <section className="rounded-2xl border border-border bg-card p-6">
        <h2 className="text-base font-semibold text-foreground">{t('selfService.title', { defaultValue: 'My employee record' })}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('selfService.notLinked', { defaultValue: 'No employee record is linked to your account yet. Ask an admin to link you (People → Edit → Linked account).' })}
        </p>
      </section>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const changes: Record<string, unknown> = {};
    if (preferredName.trim()) changes.preferredName = preferredName.trim();
    if (dateOfBirth) changes.dateOfBirth = dateOfBirth;
    if (workLocation.trim()) changes.workLocation = workLocation.trim();
    if (Object.keys(changes).length === 0 && !note.trim()) { setErr(t('selfService.nothing', { defaultValue: 'Enter at least one change or a note.' })); return; }
    try {
      await submit.mutateAsync({ changes, note: note.trim() || undefined });
      setDone(true); setPreferredName(''); setDateOfBirth(''); setWorkLocation(''); setNote('');
    } catch (e2: unknown) {
      const m = (e2 as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setErr(Array.isArray(m) ? m[0] : (m || (e2 as Error).message));
    }
  }

  return (
    <section className="rounded-2xl border border-border bg-card p-6 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-foreground">{t('selfService.title', { defaultValue: 'My employee record' })}</h2>
        <p className="text-xs text-muted-foreground mt-0.5">{t('selfService.subtitle', { defaultValue: 'Request changes to your own details — they go to HR for approval.' })}</p>
      </div>
      <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
        <Detail label={t('selfService.name', { defaultValue: 'Name' })} value={emp.legalNameLocal} />
        <Detail label={t('selfService.preferred', { defaultValue: 'Preferred' })} value={emp.preferredName ?? '—'} />
        <Detail label={t('selfService.title2', { defaultValue: 'Title' })} value={emp.jobTitle ?? '—'} />
        <Detail label={t('selfService.dob', { defaultValue: 'Date of birth' })} value={emp.dateOfBirth ? String(emp.dateOfBirth).slice(0, 10) : '—'} />
        <Detail label={t('selfService.location', { defaultValue: 'Work location' })} value={emp.workLocation ?? '—'} />
        <Detail label={t('selfService.country', { defaultValue: 'Country' })} value={emp.countryCode} />
      </dl>

      <form onSubmit={onSubmit} className="border-t border-border pt-4 space-y-3">
        <p className="text-xs font-semibold text-foreground">{t('selfService.request', { defaultValue: 'Request a change' })}</p>
        {done && <p className="text-xs text-emerald-600">{t('selfService.submitted', { defaultValue: '✓ Submitted — pending HR approval.' })}</p>}
        {err && <p className="text-xs text-rose-600">{err}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label={t('selfService.preferred', { defaultValue: 'Preferred name' })}>
            <input value={preferredName} onChange={(e) => setPreferredName(e.target.value)} placeholder={emp.preferredName ?? ''} className="ss-input" />
          </Field>
          <Field label={t('selfService.dob', { defaultValue: 'Date of birth' })}>
            <input type="date" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} className="ss-input" />
          </Field>
          <Field label={t('selfService.location', { defaultValue: 'Work location' })}>
            <input value={workLocation} onChange={(e) => setWorkLocation(e.target.value)} placeholder={emp.workLocation ?? ''} className="ss-input" />
          </Field>
        </div>
        <Field label={t('selfService.note', { defaultValue: 'Note to HR (optional)' })}>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="ss-input" />
        </Field>
        <button type="submit" disabled={submit.isPending}
          className="inline-flex h-9 items-center px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition">
          {submit.isPending ? t('common.saving', { defaultValue: 'Submitting…' }) : t('selfService.submit', { defaultValue: 'Submit request' })}
        </button>
      </form>
      <style>{`.ss-input{width:100%;border:1px solid hsl(var(--border));border-radius:0.5rem;padding:0.5rem 0.75rem;font-size:0.875rem;background:hsl(var(--card));color:hsl(var(--foreground));outline:none}.ss-input:focus{border-color:hsl(var(--primary));box-shadow:0 0 0 3px hsl(var(--primary)/0.12)}`}</style>
    </section>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</dt>
      <dd className="text-sm text-foreground mt-0.5">{value}</dd>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-muted-foreground mb-1">{label}</span>
      {children}
    </label>
  );
}
